/**
 * SPAC trust values (J1, docs/spac_watch_build.md): blank-cheque companies whose shares trade below the
 * cash held in trust for them, with the deadline to complete a deal and the annualised return to that
 * deadline, plus a paper book kept under a fixed rule. Information only: the figures come from SEC
 * filings (XBRL facts and a model's reading of the text) and must be checked against the filing before
 * acting. Nothing here is a trade signal and nothing here places an order.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, Button, Icon, Panel, SegmentedControl, StatCell, Switch, etSyd, fmt, etAndLocal } from "@/sentinel";
import { QueryBoundary, UpdatedAt, retryTransient } from "@/lib/queryState";

const isNum = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v));
const pct = (v, digits = 2, missing = "n/a") => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}${Math.abs(Number(v) * 100).toFixed(digits)}%` : missing);
const price = (v) => (isNum(v) ? `$${fmt(Number(v), "price")}` : "n/a");
const errText = (e) => fmtErr(e.response?.data?.detail) || e.message;
const DEAL_LABEL = { none: "None", agreement: "Agreement signed", vote_scheduled: "Vote scheduled", completed: "Completed", terminated: "Terminated" };
const CONF_LABEL = { high: "High", medium: "Medium", low: "Low" };

function deadlineLabel(r) {
  if (!r.deadline) return <span className="sn-muted">Not stated</span>;
  const d = r.days_to_deadline;
  const ext = r.deadline_extendable === true ? ", extendable" : r.deadline_extendable === false ? ", final" : "";
  return (
    <span title={r.deadline_note || ""}>
      {r.deadline}
      <span className="sn-small sn-faint"> {isNum(d) ? (d < 0 ? "(passed" : d === 0 ? "(today" : `(${d} days`) + `${ext})` : ""}</span>
    </span>
  );
}

function trustCell(r) {
  if (!isNum(r.trust_per_share)) return <span className="sn-muted">Not found</span>;
  return (
    <span title={r.trust_source || ""}>
      {price(r.trust_per_share)}
      {r.trust_disagree ? <> <Badge tone="warn" icon="TriangleAlert">Figures differ</Badge></> : null}
      {isNum(r.trust_age_days) ? <span className="sn-small sn-faint"> {r.trust_age_days}d old</span> : null}
    </span>
  );
}

function CompanyCell({ r }) {
  const f = r.filing || {};
  return (
    <div className="sn-stack" style={{ gap: 2 }}>
      {f.index_url ? (
        <a href={f.index_url} target="_blank" rel="noreferrer" className="sn-link" data-testid={`spacs-link-${r.cik}`}>{r.name || r.cik}</a>
      ) : <span>{r.name || r.cik}</span>}
      <span className="sn-small sn-faint">
        {f.form ? `${f.form} filed ${f.date}` : "no filing read"}{r.deal_target ? ` · deal: ${r.deal_target}` : ""}
      </span>
    </div>
  );
}

function Row({ r, owner, reread, showAll }) {
  return (
    <tr data-testid={`spacs-row-${r.cik}`} data-state={r.qualifies ? "qualifies" : r.read_status}
        style={r.qualifies ? { background: "var(--brand-dim)" } : undefined}>
      <td style={{ whiteSpace: "normal" }}><CompanyCell r={r} /></td>
      <td className="id">{r.ticker || <span className="sn-muted">None</span>}</td>
      <td className="num">
        {isNum(r.price) ? price(r.price) : <span className="sn-muted">No price</span>}
        {r.price_date ? <span className="sn-small sn-faint"> {r.price_date}</span> : null}
      </td>
      <td className="num">{trustCell(r)}</td>
      <td className="num" style={{ color: "var(--text-primary)" }}>{pct(r.discount)}</td>
      <td className="num">{deadlineLabel(r)}</td>
      <td className="num" style={{ fontWeight: r.qualifies ? 600 : undefined }}>{pct(r.annual_yield, 1)}</td>
      <td>{DEAL_LABEL[r.deal_status] || (r.read_status === "read" ? "None" : "")}</td>
      <td>{CONF_LABEL[r.confidence] || ""}</td>
      {showAll && (
        <td className="sn-small" style={{ whiteSpace: "normal" }}>
          {r.qualifies ? <Badge tone="brand" icon="Check">In the paper book's rule</Badge> : (r.reasons || []).join("; ")}
        </td>
      )}
      <td>
        {owner && (
          <Button size="sm" variant="ghost" icon="RefreshCw" loading={reread.isPending && reread.variables === r.cik}
                  onClick={() => reread.mutate(r.cik)} data-testid={`spacs-reread-${r.cik}`}>Read again</Button>
        )}
      </td>
    </tr>
  );
}

function ScreenTable({ rows, owner, reread, showAll }) {
  return (
    <div className="sn-table-wrap" data-testid={showAll ? "spacs-all-table" : "spacs-table"}>
      <table className="sn-table" aria-label="SPACs by trust value">
        <thead><tr>
          <th>Company (link to the filing)</th><th>Ticker</th><th className="r">Price</th><th className="r">Trust per share</th>
          <th className="r">Discount</th><th className="r">Deadline</th><th className="r">Annualised to deadline</th>
          <th>Deal</th><th>Reading</th>{showAll && <th>Rule</th>}<th />
        </tr></thead>
        <tbody>{rows.map((r) => <Row key={r.cik} r={r} owner={owner} reread={reread} showAll={showAll} />)}</tbody>
      </table>
    </div>
  );
}

function StatusLine({ data, at }) {
  const c = data?.latest?.counts || {};
  const b = data?.budget || {};
  const r = data?.last_result;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid="spacs-status">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="SPACs listed" value={String(c.companies ?? 0)}
                  sub={`${c.priced ?? 0} with a price, ${c.with_trust ?? 0} with a trust value`} />
        <StatCell size="sm" label="Below trust value" value={String(c.below_trust ?? 0)} sub={`${c.qualifying ?? 0} meet the paper rule`} />
        <StatCell size="sm" label="Filings not read yet" value={String(c.unread ?? 0)}
                  sub={c.errors ? `${c.errors} could not be read` : "up to 40 are read each evening"} />
        <StatCell size="sm" label="Claude spend this month" value={isNum(b.spent_usd) ? fmt(b.spent_usd, "usd") : "$0"}
                  sub={`of a ${isNum(b.budget_usd) ? fmt(b.budget_usd, "usd") : "$15"} budget${b.blocked_calls ? ` · ${b.blocked_calls} calls blocked` : ""}`} />
      </div>
      <p className="sn-small sn-muted" style={{ margin: 0 }}>
        Paper book {data?.enabled ? "on" : "paused"}.{" "}
        {data?.last_run_at ? `Last run ${etSyd(data.last_run_at)}` : "No run yet"}
        {r ? ` (${r.read} filings read, ${r.priced} priced, ${r.opened} positions opened, ${r.closed} closed${r.errors?.length ? `, ${r.errors.length} errors` : ""})` : ""}.
        {data?.universe_at ? ` The list of SPACs was refreshed ${etSyd(data.universe_at, { seconds: false })} from EDGAR (${data.universe_size ?? "?"} companies with a recent 10-Q or 10-K).` : ""}
        {" "}Runs at {etAndLocal("20:50")} on weekdays. <UpdatedAt at={at} prefix="Shown" />
      </p>
    </div>
  );
}

function ScreenPanel() {
  const { can } = useAuth();
  const owner = can("owner");
  const qc = useQueryClient();
  const [show, setShow] = useState("below");
  const q = useQuery({ queryKey: ["eq-spacs"], queryFn: api.eqSpacs, refetchInterval: 10 * 60 * 1000, retry: retryTransient });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["eq-spacs"] }); qc.invalidateQueries({ queryKey: ["eq-spacs-paper"] }); };
  const onError = (e) => toast.error(errText(e));
  const run = useMutation({
    mutationFn: () => api.eqSpacsRefresh(),
    onSuccess: (r) => { toast.success(`Run done: ${r.read} filings read, ${r.priced} priced, ${r.opened} opened, ${r.closed} closed.`); refresh(); },
    onError,
  });
  const reread = useMutation({
    mutationFn: (cik) => api.eqSpacReread(cik),
    onSuccess: (d) => {
      if (d?.read_status === "read") toast.success(`Read again: ${d.name || d.cik}.`);
      else toast.error(`Could not read ${d?.name || d?.cik}${d?.error ? `: ${d.error}` : ""}.`);
      refresh();
    },
    onError,
  });
  const toggle = useMutation({ mutationFn: (enabled) => api.eqSpacsConfig({ enabled }), onSuccess: refresh, onError });
  return (
    <Panel title="SPACs and their trust values" icon="Hourglass" data-testid="spacs-list"
           actions={
             <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
               {owner && (
                 <Switch label="Paper book" checked={q.data?.enabled !== false} onChange={(on) => toggle.mutate(on)}
                         disabled={toggle.isPending} data-testid="spacs-toggle" />
               )}
               {owner && (
                 <Button size="sm" variant="secondary" icon="RefreshCw" loading={run.isPending} onClick={() => run.mutate()}
                         data-testid="spacs-run-now">Run now</Button>
               )}
             </div>
           }>
      <QueryBoundary query={q} loading={4} what="the SPAC list" errorTestId="spacs-error" isEmpty={() => false}>
        {(data) => {
          const rows = data?.latest?.rows || [];
          const below = rows.filter((r) => isNum(r.discount) && r.discount > 0);
          const shown = show === "all" ? rows : below;
          return (
            <div className="sn-stack">
              <StatusLine data={data} at={q.dataUpdatedAt} />
              <SegmentedControl label="Which companies to show" value={show} onChange={setShow} size="sm"
                                options={[{ value: "below", label: "Trading below trust value", testId: "spacs-show-below" },
                                          { value: "all", label: "All, with the rule's reasons", testId: "spacs-show-all" }]} />
              {rows.length === 0 ? (
                <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="spacs-empty">
                  No list yet. The first run reads the SEC's list of blank-cheque companies and their latest filings; an owner can start it now.
                </p>
              ) : shown.length === 0 ? (
                <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="spacs-none-below">No SPAC with a price and a trust value is trading below its trust value today.</p>
              ) : (
                <ScreenTable rows={shown} owner={owner} reread={reread} showAll={show === "all"} />
              )}
              <p className="sn-small sn-faint" style={{ margin: 0 }}>
                Trust per share comes from the company's XBRL facts (trust balance or redemption value over the shares that can be redeemed), or
                from the figure stated in the filing's text when the facts are missing; a warning shows when the two differ by more than 2%.
                The deadline and any deal are read from the latest 10-Q or 10-K by Claude ({data?.model || "claude-sonnet-5"}, prompt {data?.prompt_version || "n/a"})
                and can be out of date when a later 8-K changed them. Annualised to deadline = (trust per share / price) raised to 365 / days, less one; it
                assumes redemption at the trust value on the deadline and ignores the interest the trust earns in the meantime. Prices are the last daily close.
              </p>
            </div>
          );
        }}
      </QueryBoundary>
    </Panel>
  );
}

// ── paper book ──────────────────────────────────────────────────────────────
function PositionsTable({ rows, label, testId }) {
  return (
    <div className="sn-table-wrap" data-testid={testId}>
      <table className="sn-table" aria-label={label}>
        <thead><tr>
          <th>Company</th><th>Ticker</th><th className="r">Opened</th><th className="r">Entry</th><th className="r">Trust at entry</th>
          <th className="r">Deadline at entry</th><th className="r">Latest or exit</th><th className="r">Return after costs</th><th className="r">Annualised</th><th>Closed because</th>
        </tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} data-testid={`spacs-pos-${p.id}`} data-state={p.status}>
              <td style={{ whiteSpace: "normal" }}>{p.name || p.cik}</td>
              <td className="id">{p.ticker || ""}</td>
              <td className="num">{p.entry_date}</td>
              <td className="num">{price(p.entry_price)}</td>
              <td className="num">{price(p.trust_at_entry)}</td>
              <td className="num">{p.deadline_at_entry || "n/a"}</td>
              <td className="num">
                {p.status === "closed" ? `${price(p.exit_price)} on ${p.exit_date}` : `${price(p.last_price)}${p.last_date ? ` on ${p.last_date}` : ""}`}
              </td>
              <td className="num" style={{ color: "var(--text-primary)" }}>{pct(p.status === "closed" ? p.net_ret : p.mark_ret)}</td>
              <td className="num">{p.status === "closed" ? pct(p.annualised, 1) : pct(p.annual_yield_at_entry, 1, "n/a")}{p.status !== "closed" ? <span className="sn-small sn-faint"> expected</span> : null}</td>
              <td className="sn-small" style={{ whiteSpace: "normal" }}>{p.exit_reason || (p.status === "open" ? "Open" : "")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaperPanel() {
  const q = useQuery({ queryKey: ["eq-spacs-paper"], queryFn: api.eqSpacsPaper, retry: retryTransient });
  return (
    <Panel title="Paper book" icon="BookOpen" data-testid="spacs-paper">
      <QueryBoundary query={q} loading={3} what="the paper book" errorTestId="spacs-paper-error" isEmpty={() => false}>
        {(data) => {
          const s = data?.summary || {};
          const rule = data?.rule || {};
          const positions = data?.positions || [];
          const open = positions.filter((p) => p.status === "open");
          const closed = positions.filter((p) => p.status === "closed");
          return (
            <div className="sn-stack">
              <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="spacs-rule">
                The rule, fixed before the first run: when a SPAC's latest close is below its trust value per share, the deadline is between
                {" "}{rule.min_days ?? 30} days and eighteen months away, the trust figure is no more than {rule.max_trust_age_days ?? 120} days old, the close is no more than {rule.max_price_age_days ?? 7} days old, the price is at least
                ${rule.min_price ?? 9} and the annualised return to the deadline is at least {Math.round((rule.min_annual_yield ?? 0.06) * 100)}%, a ${(rule.position_usd ?? 10000).toLocaleString("en-US")} paper position
                opens at that close, up to {rule.max_open ?? 20} at once. It closes when the price reaches the trust value (sold at the close), at the deadline (redeemed at the
                trust value, or sold if the close is higher), or when the share stops trading. Costs are {((rule.cost_per_side ?? 0.0025) * 100).toFixed(2)}% a side.
                Judged once after {rule.target_months ?? 12} months against the Treasury-bill return; no verdict before then.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="spacs-paper-stats">
                <StatCell size="sm" label="Open positions" value={String(s.open ?? 0)}
                          sub={isNum(s.open_mark_mean) ? `marked ${pct(s.open_mark_mean)} on average` : "none open"} />
                <StatCell size="sm" label="Closed positions" value={String(s.closed ?? 0)}
                          sub={isNum(s.share_positive) ? `${Math.round(s.share_positive * 100)}% positive` : "none closed yet"} />
                <StatCell size="sm" label="Mean return, closed" value={pct(s.mean_net)}
                          sub={isNum(s.se) ? `± ${(2 * s.se * 100).toFixed(2)}% (t ${s.t?.toFixed(2)})` : "range appears after two"} />
                <StatCell size="sm" label="Mean annualised, closed" value={pct(s.mean_annualised, 1)}
                          sub={isNum(s.months_running) ? `${s.months_running.toFixed(1)} of ${s.target_months ?? 12} months` : "not started"} />
              </div>
              {open.length > 0 && <PositionsTable rows={open} label="Open paper positions" testId="spacs-open-table" />}
              {closed.length > 0 && (
                <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
                  <span className="sn-micro">Closed ({closed.length})</span>
                  <PositionsTable rows={closed} label="Closed paper positions" testId="spacs-closed-table" />
                </div>
              )}
              {positions.length === 0 && (
                <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="spacs-paper-empty">
                  No paper position yet. One opens the first evening a SPAC meets the rule.
                </p>
              )}
            </div>
          );
        }}
      </QueryBoundary>
    </Panel>
  );
}

export default function EquitiesSpacs() {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-spacs">
      <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="spacs-intro">
        A SPAC (blank-cheque company) raises money, holds it in a trust invested in Treasury bills, and must complete a business combination
        by a deadline or return the trust to its public shareholders; at every shareholder vote a holder can also redeem each share for its share
        of the trust. When the shares trade below the trust value per share, holding them to the redemption pays the gap plus the trust's interest.
        This page lists the SPACs that file with the SEC, what their filings say, and a paper book kept under a fixed rule. The figures are read
        from filings by a model and must be checked against the filing before acting. Nothing here is a trade signal and nothing here places an order.
      </p>
      <ScreenPanel />
      <PaperPanel />
    </div>
  );
}
